import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Save, Sparkles, Loader2 } from "lucide-react";

/**
 * CompleteProfileDialog
 *
 * Modal usato dai clienti per completare i campi mancanti del profilo
 * e sbloccare gli strumenti AI. Mostra tutti i campi che possono essere
 * mancanti dalla registrazione `/gratis`:
 *   - Nome del salone (richiesto da: tutti gli AI tool)
 *   - Posizionamento (richiesto da: caption, cover, idea giornaliera,
 *     idee storie)
 *   - Città / Provincia (richiesto solo da: idee storie Pro)
 *
 * Submit fa 2 chiamate API in sequenza:
 *   1. PATCH /auth/me → name_salone, referente_*
 *   2. PATCH /client/profile → positioning, city, province
 *
 * Su successo: refresh dell'utente nel context, callback opzionale,
 * chiusura modal. Niente reload pagina — gli strumenti si sbloccano
 * in real-time.
 */
export default function CompleteProfileDialog({ open, onOpenChange, focusField, onCompleted }) {
  const { user, refreshUser } = useAuth();
  const setup = user?.profile_setup || {};

  const [nameSalone, setNameSalone] = useState(user?.name_salone || "");
  const [referenteNome, setReferenteNome] = useState(user?.referente_nome || "");
  const [referenteCognome, setReferenteCognome] = useState(user?.referente_cognome || "");
  const [positioning, setPositioning] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!nameSalone.trim()) {
      toast.error("Il nome del salone è obbligatorio");
      return;
    }
    if (!positioning.trim() || positioning.trim().length < 15) {
      toast.error("Scrivi il posizionamento in almeno 1 frase (min 15 caratteri)");
      return;
    }
    setLoading(true);
    try {
      const mePayload = {};
      if (nameSalone.trim() !== (user?.name_salone || ""))
        mePayload.name_salone = nameSalone.trim();
      if (referenteNome.trim() !== (user?.referente_nome || ""))
        mePayload.referente_nome = referenteNome.trim();
      if (referenteCognome.trim() !== (user?.referente_cognome || ""))
        mePayload.referente_cognome = referenteCognome.trim();
      if (Object.keys(mePayload).length > 0) {
        await api.patch("/auth/me", mePayload);
      }
      const profilePayload = { positioning: positioning.trim() };
      if (city.trim()) profilePayload.city = city.trim();
      if (province.trim()) profilePayload.province = province.trim();
      await api.patch("/client/profile", profilePayload);

      await refreshUser();
      toast.success("Profilo completato ✓ — strumenti AI sbloccati");
      onOpenChange(false);
      if (onCompleted) onCompleted();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="complete-profile-dialog"
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[var(--mp-primary)]" />
            Completa il profilo e sblocca gli strumenti AI
          </DialogTitle>
          <DialogDescription>
            Ti chiediamo le 3 cose minime che servono all&apos;AI per
            personalizzare le idee, le caption e le copertine sul TUO
            salone. Ti prende 1 minuto.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="cp-name" className="text-[10px] font-bold uppercase tracking-[0.2em]">
              Nome del salone <span className="text-red-600">*</span>
            </Label>
            <Input
              id="cp-name"
              data-testid="complete-profile-salone"
              autoFocus={!focusField || focusField === "name_salone"}
              value={nameSalone}
              onChange={(e) => setNameSalone(e.target.value)}
              placeholder="es. Salone Rossi · Hair Studio"
              className="mt-1.5 h-11 rounded-xl border-[var(--mp-ink)]"
              required
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cp-rn" className="text-[10px] font-bold uppercase tracking-[0.2em]">
                Nome referente
              </Label>
              <Input
                id="cp-rn"
                data-testid="complete-profile-ref-nome"
                value={referenteNome}
                onChange={(e) => setReferenteNome(e.target.value)}
                placeholder="es. Mario"
                className="mt-1.5 h-11 rounded-xl border-[var(--mp-ink)]"
              />
            </div>
            <div>
              <Label htmlFor="cp-rc" className="text-[10px] font-bold uppercase tracking-[0.2em]">
                Cognome referente
              </Label>
              <Input
                id="cp-rc"
                data-testid="complete-profile-ref-cognome"
                value={referenteCognome}
                onChange={(e) => setReferenteCognome(e.target.value)}
                placeholder="es. Rossi"
                className="mt-1.5 h-11 rounded-xl border-[var(--mp-ink)]"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="cp-pos" className="text-[10px] font-bold uppercase tracking-[0.2em]">
              Posizionamento del salone <span className="text-red-600">*</span>
            </Label>
            <textarea
              id="cp-pos"
              data-testid="complete-profile-positioning"
              autoFocus={focusField === "positioning"}
              value={positioning}
              onChange={(e) => setPositioning(e.target.value.slice(0, 800))}
              placeholder="es. Salone boutique a Milano, target donne 30-50 anni, focus su colori naturali e cura del capello. Tono raffinato, premium ma accogliente."
              rows={5}
              minLength={15}
              maxLength={800}
              required
              className="w-full mt-1.5 rounded-xl border border-[var(--mp-ink)] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--mp-yellow)] resize-none"
            />
            <div className="text-[11px] text-[var(--mp-muted)] mt-1">
              {positioning.length}/800 — descrivilo come lo presenteresti
              a un nuovo cliente. È la cosa che fa la VERA differenza tra
              un&apos;idea AI generica e una sartoriale per te.
            </div>
          </div>

          <div className="grid sm:grid-cols-[1fr_120px] gap-3">
            <div>
              <Label htmlFor="cp-city" className="text-[10px] font-bold uppercase tracking-[0.2em]">
                Città{" "}
                <span className="text-[var(--mp-muted)] text-[10px] font-normal normal-case tracking-normal">
                  (sblocca le idee storie geo-locali)
                </span>
              </Label>
              <Input
                id="cp-city"
                data-testid="complete-profile-city"
                autoFocus={focusField === "city"}
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="es. Milano"
                className="mt-1.5 h-11 rounded-xl border-[var(--mp-ink)]"
              />
            </div>
            <div>
              <Label htmlFor="cp-prov" className="text-[10px] font-bold uppercase tracking-[0.2em]">
                Provincia
              </Label>
              <Input
                id="cp-prov"
                data-testid="complete-profile-province"
                value={province}
                onChange={(e) => setProvince(e.target.value.toUpperCase().slice(0, 2))}
                placeholder="MI"
                maxLength={2}
                className="mt-1.5 h-11 rounded-xl border-[var(--mp-ink)] uppercase"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[var(--mp-border)]">
            <Button
              type="submit"
              disabled={loading}
              data-testid="complete-profile-submit"
              className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white h-11 px-6"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Salva e sblocca tutto
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="complete-profile-cancel"
              className="rounded-full border-[var(--mp-ink)] h-11"
            >
              Annulla
            </Button>
            <div className="text-[11px] text-[var(--mp-muted)] ml-auto">
              {[setup.has_salone, setup.has_positioning, setup.has_city].filter(Boolean).length}/3
              campi già compilati
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
